// Implements: F-UIX-019, F-UIX-007, F-GEN-036
/**
 * The model-settings form, as pure functions: what the admin types and what is sent.
 *
 * A cap (an output cap per tier, the message maximum) is either a positive whole number or
 * explicitly unset by its "no limit" toggle, which sends `null`. A blank field with the toggle off
 * is refused, never read as "no limit": a cost guard is not removed by an empty box.
 * Prices are typed in dollars per million tokens and turned into exact micros from their digits
 * (the ceilings editor's reader, so "1,000" is refused as ambiguous instead of guessed).
 * Every model the form chooses must be priced, and the two tiers must differ; the server checks
 * both again, plus the catalogue, and its refusal lands on the same fields (`serverErrors`).
 */

import type { ErrorDetails } from "../../../types/errors";
import { REASONING_EFFORTS } from "../../../types/modelSettings";
import type {
  EditableSettings,
  ModelSettingsRequest,
  ModelSettingsView,
  PriceEntry,
  ReasoningEffort,
  TierName,
  TierSettings,
} from "../../../types/modelSettings";
import { amountMicros } from "./ceilingForm";
import { microsDecimal } from "./money";

export interface TierDraft {
  readonly model: string;
  readonly cap: string;
  readonly noLimit: boolean;
  readonly effort: ReasoningEffort | "";
}

export interface PriceDraft {
  readonly prompt: string;
  readonly completion: string;
}

export interface SettingsDraft {
  readonly fast: TierDraft;
  readonly capable: TierDraft;
  /** Dollars per million tokens, by model id. */
  readonly prices: Readonly<Record<string, PriceDraft>>;
  readonly message: string;
  readonly messageNoLimit: boolean;
  /**
   * Mockup v3 P7: where a model's prices on screen came from, when the catalogue filled them
   * (`catalogue`) and the admin then typed over them (`edited`); absent for typed or stored ones.
   */
  readonly priceTags?: Readonly<Record<string, "catalogue" | "edited">>;
}

/**
 * Why a field is refused. The client says `required`, `positive_integer`, `same_model`,
 * `price_required` and `price_invalid`; the server adds `unknown_model`, `unpriced_model`,
 * `above_model_maximum`, `too_many_prices`, `malformed_model_id` and, for any reason it has not
 * been taught here, `rejected`.
 */
export type FieldErrorKind =
  | "required"
  | "positive_integer"
  | "same_model"
  | "price_required"
  | "price_invalid"
  | "unknown_model"
  | "unpriced_model"
  | "above_model_maximum"
  | "too_many_prices"
  | "malformed_model_id"
  | "rejected";

/**
 * The model id as typed, without what can never belong to one (UX-11 verifier finding 7):
 * whitespace anywhere and invisible format characters (Unicode `Cf`: zero-width spaces and
 * joiners, soft hyphens, byte-order marks), which a paste carries along and the server refuses
 * as a malformed id while the field looks right.
 */
export function cleanModelId(typed: string): string {
  return typed.replace(/[\p{Cf}\s]/gu, "");
}

/** Keyed by `fast.model`, `fast.cap`, `fast.effort`, the same for `capable`, `message`, `price.<model>.prompt|completion`. */
export type FieldErrors = Readonly<Record<string, FieldErrorKind>>;

export type SettingsParse =
  | { readonly ok: true; readonly body: ModelSettingsRequest }
  | { readonly ok: false; readonly errors: FieldErrors };

const WHOLE_RE = /^\d+$/;
const DEFAULT_CURRENCY = "USD";

export function priceField(model: string, part: "prompt" | "completion"): string {
  return `price.${model}.${part}`;
}

/** `250000` micros -> `"0.25"`: the stored amount as the admin would type it. */
function dollarsText(micros: number): string {
  return microsDecimal(micros).replace(/\.?0+$/, "");
}

/** A stored or catalogue price as the admin would type it: `{ prompt: "0.25", completion: "2" }`. */
export function priceDraftOf(price: PriceEntry): PriceDraft {
  return {
    prompt: dollarsText(price.prompt_micros_per_million_tokens),
    completion: dollarsText(price.completion_micros_per_million_tokens),
  };
}

function tierDraft(tier: TierSettings): TierDraft {
  return {
    model: tier.model_id,
    cap: tier.max_output_tokens === null ? "" : String(tier.max_output_tokens),
    noLimit: tier.max_output_tokens === null,
    effort: tier.reasoning_effort ?? "",
  };
}

/** The form filled with the stored settings; an unset cap is the toggle on with an empty field. */
export function draftOf(view: ModelSettingsView): SettingsDraft {
  return {
    fast: tierDraft(view.tiers.fast),
    capable: tierDraft(view.tiers.capable),
    prices: Object.fromEntries(Object.entries(view.prices).map(([model, price]) => [model, priceDraftOf(price)])),
    message: view.message_max_chars === null ? "" : String(view.message_max_chars),
    messageNoLimit: view.message_max_chars === null,
  };
}

/** The distinct, trimmed, non-blank models the form chooses, in tier order. */
export function chosenModels(draft: SettingsDraft): string[] {
  const models = [draft.fast.model.trim(), draft.capable.model.trim()].filter((model) => model !== "");
  return [...new Set(models)];
}

/** The keys of the controls the form shows for `draft`: a refusal on any other key has no field to land on. */
export function shownFields(draft: SettingsDraft): ReadonlySet<string> {
  const prices = chosenModels(draft).flatMap((model) => [priceField(model, "prompt"), priceField(model, "completion")]);
  return new Set([
    "fast.model",
    "fast.cap",
    "fast.effort",
    "capable.model",
    "capable.cap",
    "capable.effort",
    "message",
    // The prices card as a whole: the server names `prices` for the list (too many, a malformed key).
    "prices",
    ...prices,
  ]);
}

/** A positive whole number, `null` when the toggle says no limit, `undefined` when refused. */
function capOf(text: string, noLimit: boolean): number | null | undefined {
  if (noLimit) return null;
  const value = text.trim();
  if (!WHOLE_RE.test(value)) return undefined;
  const count = Number(value);
  return Number.isSafeInteger(count) && count > 0 ? count : undefined;
}

function priceOf(
  model: string,
  draft: PriceDraft | undefined,
  base: ModelSettingsView,
  errors: Record<string, FieldErrorKind>,
): PriceEntry | undefined {
  const prompt = amountMicros(draft?.prompt ?? "");
  const completion = amountMicros(draft?.completion ?? "");
  for (const [part, micros, text] of [
    ["prompt", prompt, draft?.prompt ?? ""],
    ["completion", completion, draft?.completion ?? ""],
  ] as const) {
    if (text.trim() === "") errors[priceField(model, part)] = "price_required";
    else if (typeof micros !== "number") errors[priceField(model, part)] = "price_invalid";
  }
  if (typeof prompt !== "number" || typeof completion !== "number") return undefined;
  return {
    prompt_micros_per_million_tokens: prompt,
    completion_micros_per_million_tokens: completion,
    currency: base.prices[model]?.currency ?? DEFAULT_CURRENCY,
  };
}

function tierOf(
  name: TierName,
  draft: TierDraft,
  errors: Record<string, FieldErrorKind>,
): TierSettings | undefined {
  const model = draft.model.trim();
  if (model === "") errors[`${name}.model`] = "required";
  const cap = capOf(draft.cap, draft.noLimit);
  if (cap === undefined) errors[`${name}.cap`] = "positive_integer";
  if (model === "" || cap === undefined) return undefined;
  return { model_id: model, max_output_tokens: cap, reasoning_effort: draft.effort === "" ? null : draft.effort };
}

/** The body to send (from the revision the form was loaded from), or the refused fields and why. */
export function parseDraft(draft: SettingsDraft, base: ModelSettingsView): SettingsParse {
  const errors: Record<string, FieldErrorKind> = {};
  const fast = tierOf("fast", draft.fast, errors);
  const capable = tierOf("capable", draft.capable, errors);
  const fastModel = draft.fast.model.trim();
  if (fastModel !== "" && fastModel === draft.capable.model.trim()) errors["capable.model"] = "same_model";

  // Every chosen model carries its typed price; the prices of the others stay as the server holds them.
  const models = chosenModels(draft);
  // A `Map` while the admin's model ids are keys, an object only at the edge: assigning
  // `object["__proto__"]` would set a prototype instead of an entry, and the price would vanish.
  const prices = new Map<string, PriceEntry>(Object.entries(base.prices).filter(([model]) => !models.includes(model)));
  for (const model of models) {
    const price = priceOf(model, draft.prices[model], base, errors);
    if (price !== undefined) prices.set(model, price);
  }

  const message = capOf(draft.message, draft.messageNoLimit);
  if (message === undefined) errors.message = "positive_integer";

  if (Object.keys(errors).length > 0 || fast === undefined || capable === undefined || message === undefined) {
    return { ok: false, errors: ordered(errors) };
  }
  return {
    ok: true,
    body: { expected_revision: base.revision, tiers: { fast, capable }, prices: Object.fromEntries(prices), message_max_chars: message },
  };
}

/** The refusals in the order the form shows its fields. */
function ordered(errors: Record<string, FieldErrorKind>): FieldErrors {
  const rank = (key: string): number => {
    if (key.startsWith("fast.")) return 0;
    if (key.startsWith("capable.")) return 1;
    if (key.startsWith("price.")) return 2;
    return 3;
  };
  return Object.fromEntries(
    Object.entries(errors)
      .map(([key, kind], index) => ({ key, kind, index }))
      .sort((a, b) => rank(a.key) - rank(b.key) || a.index - b.index)
      .map(({ key, kind }) => [key, kind]),
  );
}

function priceKey(price: PriceEntry | undefined): string {
  return price === undefined
    ? ""
    : `${String(price.prompt_micros_per_million_tokens)}/${String(price.completion_micros_per_million_tokens)}/${price.currency}`;
}

function unset(before: number | null, after: number | null): boolean {
  return before !== null && after === null;
}

export interface ConfirmReasons {
  readonly pricesChanged: boolean;
  /** Any cap moved: raised, lowered, set or unset. */
  readonly capChanged: boolean;
  /** A cap that was set becomes unset: the application stops limiting it. */
  readonly capUnset: boolean;
  /** A tier points at another model. */
  readonly modelChanged: boolean;
}

/** Why saving asks for a confirmation: a price, a cap or a tier model changed. */
export function confirmReasons(base: ModelSettingsView, body: ModelSettingsRequest): ConfirmReasons {
  const models = new Set([...Object.keys(base.prices), ...Object.keys(body.prices)]);
  const pricesChanged = [...models].some((model) => priceKey(base.prices[model]) !== priceKey(body.prices[model]));
  const caps: readonly (readonly [number | null, number | null])[] = [
    [base.tiers.fast.max_output_tokens, body.tiers.fast.max_output_tokens],
    [base.tiers.capable.max_output_tokens, body.tiers.capable.max_output_tokens],
    [base.message_max_chars, body.message_max_chars],
  ];
  return {
    pricesChanged,
    capChanged: caps.some(([before, after]) => before !== after),
    capUnset: caps.some(([before, after]) => unset(before, after)),
    modelChanged:
      base.tiers.fast.model_id !== body.tiers.fast.model_id || base.tiers.capable.model_id !== body.tiers.capable.model_id,
  };
}

const TIER_FIELD_RE = /^tiers\.(fast|capable)\.(model_id|max_output_tokens|reasoning_effort)$/;
const TIER_PART = { model_id: "model", max_output_tokens: "cap", reasoning_effort: "effort" } as const;
const PRICE_FIELD_RE = /^prices\.(.+)$/;

function kindOf(reason: string): FieldErrorKind {
  switch (reason) {
    case "unknown_model":
    case "unpriced_model":
    case "above_model_maximum":
    case "too_many_prices":
    case "malformed_model_id":
      return reason;
    case "same_model_both_tiers":
      return "same_model";
    default:
      return "rejected";
  }
}

/** The field a `422` names, in the form's own keys; nothing when it names none the form knows. */
export function serverErrors(details: ErrorDetails | null): FieldErrors {
  if (details === null) return {};
  const kind = kindOf(details.reason);
  if (details.field === "message_max_chars") return { message: kind };
  if (details.field === "tiers") return { "capable.model": kind };
  if (details.field === "prices") return { prices: kind };
  const tier = TIER_FIELD_RE.exec(details.field);
  if (tier !== null) {
    const name = tier[1] ?? "fast";
    const part = TIER_PART[(tier[2] ?? "model_id") as keyof typeof TIER_PART];
    return { [`${name}.${part}`]: kind };
  }
  const price = PRICE_FIELD_RE.exec(details.field);
  if (price !== null) return { [priceField(price[1] ?? "", "prompt")]: kind };
  return {};
}

/**
 * What the model field suggests (UX-11): every model the configuration prices or a tier uses, once,
 * in code-point order. The provider catalogue is a separate picker (mockup v3 P7), so these are only
 * suggestions: any id may be typed, and the server checks it against its catalogue.
 */
export function suggestedModels(view: ModelSettingsView): string[] {
  const models = new Set([...Object.keys(view.prices), view.tiers.fast.model_id, view.tiers.capable.model_id]);
  return [...models].filter((model) => model !== "").sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
}

/** The values a save or a preview sends, out of the stored settings. */
export function editableOf(view: ModelSettingsView): EditableSettings {
  return { tiers: view.tiers, prices: view.prices, message_max_chars: view.message_max_chars };
}

/** One text per set of values, whatever the order of the prices: what a cost preview belongs to. */
export function settingsKey(settings: EditableSettings): string {
  const tier = ({ model_id, max_output_tokens, reasoning_effort }: TierSettings): unknown[] => [
    model_id,
    max_output_tokens,
    reasoning_effort,
  ];
  const prices = Object.keys(settings.prices)
    .sort()
    .map((model) => [model, priceKey(settings.prices[model])]);
  return JSON.stringify([tier(settings.tiers.fast), tier(settings.tiers.capable), prices, settings.message_max_chars]);
}

export { REASONING_EFFORTS };
